/**
 * @fileoverview ClubOS Youth Academy Dashboard
 * Real-time KPIs: players, attendance, fees, injuries, batch utilization.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";

import PropTypes from "prop-types";

import { academyService } from "api";
import FootballLoader from "components/FootballLoader";

const AGE_GROUP_COLORS = {
  U8: "#4CAF50", U10: "#8BC34A", U12: "#03A9F4",
  U14: "#9C27B0", U16: "#FF9800", U18: "#F44336", Senior: "#607D8B",
};

const SEVERITY_COLOR = { Minor: "success", Moderate: "warning", Severe: "error" };
const RECOVERY_COLOR = { Active: "error", Recovering: "warning", Cleared: "success" };

// ── Age group ring chart (CSS-based donut) ───────────────────────────────────
function AgeGroupBar({ group, count, total }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <SoftBox display="flex" alignItems="center" mb={1}>
      <SoftBox
        width={10} height={10} borderRadius="50%" mr={1} flexShrink={0}
        sx={{ backgroundColor: AGE_GROUP_COLORS[group] ?? "#90A4AE" }}
      />
      <SoftTypography variant="caption" color="text" sx={{ minWidth: 36 }}>{group}</SoftTypography>
      <SoftBox flex={1} mx={1}>
        <LinearProgress
          variant="determinate" value={pct}
          sx={{ height: 6, borderRadius: 3,
            backgroundColor: "rgba(0,0,0,0.06)",
            "& .MuiLinearProgress-bar": { backgroundColor: AGE_GROUP_COLORS[group] ?? "#90A4AE" } }}
        />
      </SoftBox>
      <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ minWidth: 24, textAlign: "right" }}>
        {count}
      </SoftTypography>
    </SoftBox>
  );
}

AgeGroupBar.propTypes = {
  group: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
};

// ── Batch utilization card ───────────────────────────────────────────────────
function BatchBar({ name, ageGroup, enrolled, capacity, pct }) {
  const color = pct >= 90 ? "#F44336" : pct >= 70 ? "#FF9800" : "#4CAF50";
  return (
    <SoftBox mb={1.5}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <SoftBox>
          <SoftTypography variant="caption" fontWeight="medium" color="dark" display="block" lineHeight={1.2}>
            {name}
          </SoftTypography>
          <SoftTypography variant="caption" color="text">{ageGroup}</SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color }}>
          {enrolled}/{capacity}
        </SoftTypography>
      </SoftBox>
      <LinearProgress
        variant="determinate" value={Math.min(pct, 100)}
        sx={{ height: 5, borderRadius: 3, backgroundColor: "rgba(0,0,0,0.06)",
          "& .MuiLinearProgress-bar": { backgroundColor: color } }}
      />
    </SoftBox>
  );
}

BatchBar.propTypes = {
  name: PropTypes.string.isRequired,
  ageGroup: PropTypes.string.isRequired,
  enrolled: PropTypes.number.isRequired,
  capacity: PropTypes.number.isRequired,
  pct: PropTypes.number.isRequired,
};

// ── Fee status pill row ───────────────────────────────────────────────────────
function FeeStatusRow({ status, count, amount }) {
  const colors = { Paid: "success", Partial: "warning", Due: "error", Waived: "info" };
  return (
    <SoftBox display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
      <SoftBadge badgeContent={status} color={colors[status] ?? "default"} size="xs" container />
      <SoftTypography variant="caption" color="text">{count} students</SoftTypography>
      <SoftTypography variant="caption" fontWeight="medium" color="dark">
        ₹{Number(amount).toLocaleString("en-IN")}
      </SoftTypography>
    </SoftBox>
  );
}

FeeStatusRow.propTypes = {
  status: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

// ── Clickable KPI card wrapper ────────────────────────────────────────────────
function ClickableStatisticsCard({ route, ...props }) {
  const navigate = useNavigate();
  return (
    <div onClick={() => navigate(route)} style={{ cursor: "pointer" }}>
      <MiniStatisticsCard {...props} />
    </div>
  );
}

ClickableStatisticsCard.propTypes = {
  route: PropTypes.string.isRequired,
};

// ── Main component ────────────────────────────────────────────────────────────
function AcademyDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    academyService.getDashboard()
      .then((response) => {
        if (response && typeof response === 'object') {
          // Provide defaults for all expected fields
          const normalized = {
            totalPlayers: response.totalPlayers ?? 0,
            activePlayers: response.activePlayers ?? 0,
            trialPlayers: response.trialPlayers ?? 0,
            inactivePlayers: response.inactivePlayers ?? 0,
            totalBatches: response.totalBatches ?? 0,
            activeBatches: response.activeBatches ?? 0,
            totalCoaches: response.totalCoaches ?? 0,
            todayAttendancePct: response.todayAttendancePct ?? 0,
            last30DaysAttendancePct: response.last30DaysAttendancePct ?? 0,
            thisMonthCollected: response.thisMonthCollected ?? 0,
            thisMonthPending: response.thisMonthPending ?? 0,
            thisMonthTotal: response.thisMonthTotal ?? 0,
            collectionRate: response.collectionRate ?? 0,
            activeInjuries: response.activeInjuries ?? 0,
            clearedThisMonth: response.clearedThisMonth ?? 0,
            byAgeGroup: Array.isArray(response.byAgeGroup) ? response.byAgeGroup : [],
            batchUtilization: Array.isArray(response.batchUtilization) ? response.batchUtilization : [],
            monthlyRevenue: Array.isArray(response.monthlyRevenue) ? response.monthlyRevenue : [],
            monthlyAttendance: Array.isArray(response.monthlyAttendance) ? response.monthlyAttendance : [],
            feeStatus: Array.isArray(response.feeStatus) ? response.feeStatus : [],
            activeInjuryList: Array.isArray(response.activeInjuryList) ? response.activeInjuryList : [],
            topDefaulters: Array.isArray(response.topDefaulters) ? response.topDefaulters : [],
          };
          setData(normalized);
        } else {
          setError("Invalid response format");
        }
      })
      .catch((e) => { console.error(e); setError("Unable to load academy data"); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <FootballLoader label="Loading academy dashboard…" minHeight={200} />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <SoftTypography variant="h6" color="error">{error ?? "No data available"}</SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  // ── Chart data ─────────────────────────────────────────────────────────────
  const revenueChart = {
    labels: (data.monthlyRevenue ?? []).map((m) => m.month),
    datasets: [
      { label: "Collected", data: (data.monthlyRevenue ?? []).map((m) => m.collected), color: "success" },
      { label: "Pending",   data: (data.monthlyRevenue ?? []).map((m) => m.pending),   color: "error" },
    ],
  };

  const attendanceChart = {
    labels: (data.monthlyAttendance ?? []).map((m) => m.month),
    datasets: [{
      label: "Attendance %",
      data: (data.monthlyAttendance ?? []).map((m) => m.presentPct),
      color: "info",
    }],
  };

  const collectionRate = data.collectionRate ?? 0;
  const collectionColor = collectionRate >= 85 ? "success" : collectionRate >= 60 ? "warning" : "error";

  const totalPlayers = data.totalPlayers ?? 0;
  const byAgeGroup   = data.byAgeGroup   ?? [];
  const batchUtil    = data.batchUtilization ?? [];
  const feeStatus    = data.feeStatus    ?? [];
  const injuries     = data.activeInjuryList ?? [];
  const defaulters   = data.topDefaulters ?? [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={1}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              Youth Academy Dashboard
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Mohammedan Sporting Club · Season 2025-26
            </SoftTypography>
          </SoftBox>
          <Chip
            icon={<Icon sx={{ fontSize: "1rem !important" }}>school</Icon>}
            label={`${data.activePlayers} Active Students`}
            size="small"
            sx={{ backgroundColor: "rgba(26,115,232,0.1)", color: "info.main", fontWeight: 600 }}
          />
        </SoftBox>

        {/* ── Top KPI row ──────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <ClickableStatisticsCard
                route="/academy/players"
                title={{ text: "Total Players" }}
                count={String(data.totalPlayers)}
                percentage={{ color: "info", text: `${data.activePlayers} active` }}
                icon={{ color: "info", component: "groups" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ClickableStatisticsCard
                route="/academy/batches"
                title={{ text: "Active Batches" }}
                count={String(data.activeBatches)}
                percentage={{ color: "success", text: `${data.totalCoaches} coaches` }}
                icon={{ color: "success", component: "sports_soccer" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ClickableStatisticsCard
                route="/academy/attendance"
                title={{ text: "Attendance (30d)" }}
                count={`${data.last30DaysAttendancePct}%`}
                percentage={{ color: data.last30DaysAttendancePct >= 75 ? "success" : "warning", text: `today ${data.todayAttendancePct}%` }}
                icon={{ color: "primary", component: "fact_check" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ClickableStatisticsCard
                route="/academy/fees"
                title={{ text: "Fee Collection" }}
                count={`${collectionRate}%`}
                percentage={{ color: collectionColor, text: `₹${Number(data.thisMonthPending).toLocaleString("en-IN")} pending` }}
                icon={{ color: collectionColor, component: "payments" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Second KPI row ────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <ClickableStatisticsCard
                route="/academy/fees"
                title={{ text: "Monthly Collected" }}
                count={`₹${Number(data.thisMonthCollected).toLocaleString("en-IN")}`}
                percentage={{ color: "success", text: "this month" }}
                icon={{ color: "success", component: "account_balance_wallet" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ClickableStatisticsCard
                route="/academy/injuries"
                title={{ text: "Active Injuries" }}
                count={String(data.activeInjuries)}
                percentage={{ color: data.activeInjuries === 0 ? "success" : "warning", text: `${data.clearedThisMonth} cleared this month` }}
                icon={{ color: data.activeInjuries > 3 ? "error" : "warning", component: "healing" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ClickableStatisticsCard
                route="/academy/players"
                title={{ text: "Trial Players" }}
                count={String(data.trialPlayers)}
                percentage={{ color: "info", text: "awaiting assessment" }}
                icon={{ color: "info", component: "person_search" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ClickableStatisticsCard
                route="/academy/players"
                title={{ text: "Inactive Players" }}
                count={String(data.inactivePlayers)}
                percentage={{ color: "secondary", text: "on leave/inactive" }}
                icon={{ color: "secondary", component: "person_off" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Charts row ───────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <VerticalBarChart
                title="Monthly Fee Collection"
                description="Collected vs Pending fees over the last 6 months"
                chart={revenueChart}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="bold" mb={0.5}>Age Group Distribution</SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mb={2}>
                    Active students across all groups
                  </SoftTypography>
                  {byAgeGroup.map((g) => (
                    <AgeGroupBar key={g.ageGroup} group={g.ageGroup} count={g.count} total={data.activePlayers} />
                  ))}
                  {byAgeGroup.length === 0 && (
                    <SoftTypography variant="caption" color="text">No data</SoftTypography>
                  )}
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Attendance trend + Batch utilization ─────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <GradientLineChart
                title="Monthly Attendance Trend"
                description="% students present per month"
                chart={attendanceChart}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="bold" mb={0.5}>Batch Utilization</SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mb={2}>
                    Enrolled / Capacity per batch
                  </SoftTypography>
                  {batchUtil.map((b) => (
                    <BatchBar key={b.batchName} {...b} />
                  ))}
                  {batchUtil.length === 0 && (
                    <SoftTypography variant="caption" color="text">No batches configured</SoftTypography>
                  )}
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Fee status + Active injuries + Defaulters ─────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>

            {/* Fee status breakdown */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="bold" mb={0.5}>Fee Status — This Month</SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mb={1.5}>
                    Current month collection breakdown
                  </SoftTypography>
                  <Divider sx={{ my: 1 }} />
                  {feeStatus.map((f) => (
                    <FeeStatusRow key={f.status} status={f.status} count={f.count} amount={f.amount} />
                  ))}
                  {feeStatus.length === 0 && (
                    <SoftTypography variant="caption" color="text">No fee data this month</SoftTypography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <SoftBox display="flex" justifyContent="space-between">
                    <SoftTypography variant="caption" fontWeight="bold">Total Due</SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                      ₹{Number(data.thisMonthTotal).toLocaleString("en-IN")}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>

            {/* Active injuries */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <SoftTypography variant="h6" fontWeight="bold">Active Injuries</SoftTypography>
                    {data.activeInjuries > 0 && (
                      <SoftBadge badgeContent={`${data.activeInjuries} active`} color="error" size="xs" container />
                    )}
                  </SoftBox>
                  {injuries.length === 0 ? (
                    <SoftBox display="flex" alignItems="center" gap={1} mt={2}>
                      <Icon sx={{ color: "success.main" }}>check_circle</Icon>
                      <SoftTypography variant="caption" color="text">No active injuries</SoftTypography>
                    </SoftBox>
                  ) : (
                    injuries.slice(0, 5).map((inj) => (
                      <SoftBox key={inj.id} py={1} borderBottom="1px solid #f0f0f0">
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                          <SoftTypography variant="caption" fontWeight="medium" color="dark">
                            {inj.playerName}
                          </SoftTypography>
                          <SoftBadge badgeContent={inj.severity} color={SEVERITY_COLOR[inj.severity] ?? "info"} size="xs" container />
                        </SoftBox>
                        <SoftTypography variant="caption" color="text">
                          {inj.injuryType} · {inj.bodyPart}
                        </SoftTypography>
                        {inj.expectedReturnDate && (
                          <SoftTypography variant="caption" color="secondary" display="block">
                            Return: {inj.expectedReturnDate}
                          </SoftTypography>
                        )}
                      </SoftBox>
                    ))
                  )}
                </SoftBox>
              </Card>
            </Grid>

            {/* Top fee defaulters */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>Fee Defaulters</SoftTypography>
                  {defaulters.length === 0 ? (
                    <SoftBox display="flex" alignItems="center" gap={1} mt={2}>
                      <Icon sx={{ color: "success.main" }}>check_circle</Icon>
                      <SoftTypography variant="caption" color="text">No outstanding dues</SoftTypography>
                    </SoftBox>
                  ) : (
                    defaulters.slice(0, 6).map((d) => (
                      <SoftBox key={d.playerId} py={0.75} borderBottom="1px solid #f0f0f0"
                        display="flex" justifyContent="space-between" alignItems="center">
                        <SoftBox>
                          <SoftTypography variant="caption" fontWeight="medium" color="dark" display="block" lineHeight={1.2}>
                            {d.playerName}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text">{d.batchName}</SoftTypography>
                        </SoftBox>
                        <SoftBox textAlign="right">
                          <SoftTypography variant="caption" fontWeight="bold" color="error" display="block" lineHeight={1.2}>
                            ₹{Number(d.outstanding).toLocaleString("en-IN")}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text">{d.monthsOverdue}m overdue</SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    ))
                  )}
                </SoftBox>
              </Card>
            </Grid>

          </Grid>
        </SoftBox>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan Sporting Club" }} links={[]} />
    </DashboardLayout>
  );
}

export default AcademyDashboard;
